import {
  PageHeader,
  PageSubheader,
  PageSubtitle,
  PageTitle,
} from "@fp/ui/components/base/page-layout/page-layout";
import { useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { HeaderActions } from "./header-actions";
import { PageBackButton, type PageTarget } from "./page-back-button";

type AdminPageHeaderProps = {
  title: string;
  subtitle?: string;
  parent?: PageTarget;
  titleIcon?: ReactNode;
  titleActions?: ReactNode;
  footer?: ReactNode;
};

const titleClassName = "text-3xl font-medium";

function TitleBlock({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <PageTitle title={title} className={titleClassName} />
      {subtitle ? <PageSubtitle subtitle={subtitle} /> : null}
    </div>
  );
}

export function AdminPageHeader({
  title,
  subtitle,
  parent,
  titleIcon,
  titleActions,
  footer,
}: AdminPageHeaderProps) {
  const href = useRouterState({ select: (state) => state.location.href });
  const from: PageTarget = { url: href, label: title };

  if (parent) {
    return (
      <>
        <PageHeader>
          <PageBackButton fallbackUrl={parent.url}>
            {parent.label}
          </PageBackButton>
          <HeaderActions from={from} />
        </PageHeader>
        <PageSubheader>
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {titleIcon}
              <TitleBlock title={title} subtitle={subtitle} />
            </div>
            {titleActions ? (
              <div className="flex shrink-0 items-center gap-1">
                {titleActions}
              </div>
            ) : null}
          </div>
          {footer}
        </PageSubheader>
      </>
    );
  }

  return (
    <>
      <PageHeader>
        <TitleBlock title={title} subtitle={subtitle} />
        <HeaderActions from={from} />
      </PageHeader>
      {titleActions || footer ? (
        <PageSubheader>
          {titleActions ? (
            <div className="flex items-center justify-end gap-2">
              {titleActions}
            </div>
          ) : null}
          {footer}
        </PageSubheader>
      ) : null}
    </>
  );
}
